import React, { useState } from 'react';
import OnyxLogo from '@/assets/images/ONYX-Logo-Black.svg';

// Color scheme type definitions for login page
type LoginColorScheme =
  | 'bright-on-dark'
  | 'dark-on-bright'
  | 'white-on-warm-grey'
  | 'sky-on-white'
  | 'purple-on-warm-grey';

interface LoginColorConfig {
  background: string;
  pattern: string;
  text: string;
}

// ONYX Brand Guide color combinations optimized for login experience
const loginColorSchemes: Record<LoginColorScheme, LoginColorConfig> = {
  'bright-on-dark': {
    background: 'bg-onyx-dark-blue', // #0b053b
    pattern: 'text-onyx-bright-blue', // #004dff
    text: 'text-white',
  },
  'dark-on-bright': {
    background: 'bg-onyx-bright-blue', // #004dff
    pattern: 'text-onyx-dark-blue', // #0b053b
    text: 'text-white',
  },
  'white-on-warm-grey': {
    background: 'bg-onyx-warm-grey', // #e5e2e8
    pattern: 'text-white', // #ffffff
    text: 'text-onyx-dark-blue',
  },
  'sky-on-white': {
    background: 'bg-white', // #ffffff
    pattern: 'text-onyx-sky-blue', // #c8f2f2
    text: 'text-onyx-dark-blue',
  },
  'purple-on-warm-grey': {
    background: 'bg-onyx-warm-grey', // #e5e2e8
    pattern: 'text-onyx-purple', // #353668
    text: 'text-onyx-dark-blue',
  },
};

interface LoginDataPatternProps {
  title: string;
  subtitle?: string;
  colorScheme?: LoginColorScheme;
  height?: 'sm' | 'md' | 'lg' | 'xl' | 'screen';
  interactive?: boolean;
  className?: string;
}

const LoginDataPattern: React.FC<LoginDataPatternProps> = ({
  title,
  subtitle,
  colorScheme = 'dark-on-bright',
  height = 'screen',
  interactive = true,
  className = '',
}) => {
  const colors = loginColorSchemes[colorScheme];

  // Mouse tracking state for interactive radial center
  const [mousePosition, setMousePosition] = useState({ x: 75, y: 65 });
  const [isHovering, setIsHovering] = useState(false);

  const heightClasses = {
    sm: 'h-32',
    md: 'h-48',
    lg: 'h-64',
    xl: 'h-80',
    screen: 'min-h-screen',
  };

  // Handle mouse movement for interactive pattern
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMousePosition({ x, y });
  };

  const handleMouseEnter = () => {
    if (!interactive) return;
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    if (!interactive) return;
    setIsHovering(false);
    setMousePosition({ x: 75, y: 65 });
  };

  // Generate data pattern grid
  const generatePatternIcons = () => {
    const icons = [];

    const safeMargin = 8;
    const availableWidth = 100 - 2 * safeMargin;
    const availableHeight = 100 - 2 * safeMargin;

    // Grid calculations based on height
    const cols = height === 'screen' ? 20 : 16;
    const rows = height === 'screen' ? 15 : 8;

    // Grid spacing
    const colSpacing = availableWidth / (cols - 1);
    const rowSpacing = availableHeight / (rows - 1);

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = safeMargin + col * colSpacing;
        const y = safeMargin + row * rowSpacing;

        // Edge fade calculations
        const distanceFromEdgeX = Math.min(x, 100 - x);
        const distanceFromEdgeY = Math.min(y, 100 - y);
        const distanceFromEdge = Math.min(distanceFromEdgeX, distanceFromEdgeY);

        const fadeZone = 12;
        let edgeFade = 1;
        if (distanceFromEdge < fadeZone) {
          const fadeRatio = distanceFromEdge / fadeZone;
          edgeFade = Math.max(0.4, fadeRatio * fadeRatio * (3 - 2 * fadeRatio));
        }

        // Interactive radial effect
        let sizeVariation = 1;
        if (interactive) {
          const radialCenterX = mousePosition.x;
          const radialCenterY = mousePosition.y;
          const centerDistanceX = Math.abs(x - radialCenterX);
          const centerDistanceY = Math.abs(y - radialCenterY);
          const centerDistance = Math.sqrt(
            centerDistanceX * centerDistanceX + centerDistanceY * centerDistanceY
          );

          const maxDistance = 60;
          sizeVariation = Math.max(0.4, 1.0 - (centerDistance / maxDistance) * 0.6);
        }

        const scale = 0.85 * sizeVariation;

        // Opacity calculations
        const baseOpacity =
          colorScheme === 'sky-on-white'
            ? 0.6
            : colorScheme === 'bright-on-dark'
              ? 1.0
              : colorScheme === 'dark-on-bright'
                ? 0.55
                : colorScheme === 'white-on-warm-grey'
                  ? 0.8
                  : colorScheme === 'purple-on-warm-grey'
                    ? 0.6
                    : 0.35;
        const opacity = baseOpacity * edgeFade;

        if (opacity < 0.12) continue;

        icons.push(
          <div
            key={`${row}-${col}${interactive ? `-${mousePosition.x}-${mousePosition.y}` : ''}`}
            className="absolute pointer-events-none"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: `translate(-50%, -50%) scale(${scale})`,
              opacity: opacity,
              transition: interactive
                ? isHovering
                  ? 'transform 0.1s ease, opacity 0.1s ease'
                  : 'transform 0.3s ease, opacity 0.3s ease'
                : 'none',
            }}
          >
            <svg
              className={`w-5 h-5 ${colors.pattern}`}
              viewBox="0 0 850.4 850.4"
              style={{
                filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.08))',
                fill: 'currentColor',
              }}
              aria-hidden="true"
            >
              <path d="M588.5,504.3c-23.3-23.3-61-23.3-84.2,0h0c-23.3,23.2-23.3,60.9,0,84.2,0,0,0,0,0,0l240.1,240.1c11.2,11.2,26.3,17.5,42.1,17.4,15.8,0,30.9-6.3,42.1-17.4,23.2-23.3,23.2-60.9,0-84.2l-240.1-240.1Z" />
              <path d="M261.5,504.3L21.4,744.3c-23.3,23.3-23.3,61,0,84.2,0,0,0,0,0,0,11.2,11.2,26.3,17.4,42.1,17.4,15.8,0,31-6.3,42.1-17.4l240-240.1c23.3-23.3,23.3-61,0-84.2-23.3-23.2-61-23.2-84.2,0" />
              <path d="M546.4,363.2c15.8,0,31-6.3,42.1-17.4l240.1-240.1c23.2-23.2,23.3-60.9,0-84.2,0,0,0,0,0,0-23.2-23.3-60.9-23.3-84.2,0,0,0,0,0,0,0l-240.1,240.1c-23.2,23.2-23.3,60.9,0,84.1,0,0,0,0,0,0,11.1,11.2,26.3,17.5,42.1,17.4" />
              <path d="M105.7,21.4c-23.3-23.3-61-23.3-84.2,0-23.3,23.3-23.3,61,0,84.2l240.1,240.1c11.2,11.2,26.3,17.5,42.1,17.4,15.8,0,30.9-6.3,42.1-17.4,23.3-23.2,23.3-60.9,0-84.2,0,0,0,0,0,0L105.7,21.4Z" />
              <path d="M425,364.9c-33.1,0-60,26.9-60,60h0c0,33.2,26.9,60,60,60h0c33.1,0,60-26.8,60-60h0c0-33.2-26.8-60-60-60h0" />
            </svg>
          </div>
        );
      }
    }

    return icons;
  };

  return (
    <div
      className={`relative overflow-hidden ${colors.background} ${heightClasses[height]} ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="presentation"
    >
      {/* Pattern Layer */}
      <div className="absolute inset-0" aria-hidden="true">
        {generatePatternIcons()}
      </div>

      {/* Content Layer */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <h1
                className={`text-4xl md:text-5xl lg:text-6xl font-fk-light ${colors.text} leading-tight`}
              >
                {title}
              </h1>
              <span
                className={`text-2xl md:text-3xl lg:text-4xl font-fk-light ${colors.text} opacity-80`}
              >
                by
              </span>
              <div className="flex items-center">
                <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24">
                  <img
                    src={OnyxLogo}
                    alt="ONYX"
                    className={`w-full h-full ${colorScheme === 'bright-on-dark' ? 'filter invert' : ''}`}
                  />
                </div>
              </div>
            </div>
            {subtitle && (
              <p className={`mt-4 text-lg md:text-xl ${colors.text} opacity-90 font-fk-regular`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Gradient overlay for better text contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent pointer-events-none" />
    </div>
  );
};

export default LoginDataPattern;
